import { useState, useEffect } from 'react';
import { Calendar, Check } from 'lucide-react';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface SchedulingIndicatorProps {
  installationId: number;
  schedulingRequired: boolean;
  compact?: boolean;
}

export function SchedulingIndicator({
  installationId,
  schedulingRequired,
  compact = false,
}: SchedulingIndicatorProps) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [localValue, setLocalValue] = useState(schedulingRequired);

  // Sync local state when props change (e.g., after query invalidation)
  useEffect(() => {
    setLocalValue(schedulingRequired);
  }, [schedulingRequired]);

  const handleToggle = async (e: React.MouseEvent) => {
    // Stop propagation to prevent drag/click on parent card
    e.stopPropagation();
    e.preventDefault();

    if (isUpdating) return;

    const newValue = !localValue;

    // Optimistic update
    setLocalValue(newValue);
    setIsUpdating(true);

    try {
      await apiRequest({
        method: 'PATCH',
        url: `/api/installations/${installationId}`,
        data: { schedulingRequired: newValue },
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/installations/grid-view'] });
      queryClient.invalidateQueries({ queryKey: ['/api/installations'] });
    } catch (error: any) {
      // Revert on error
      setLocalValue(!newValue);
      toast({
        title: 'Update failed',
        description: error?.message || 'Failed to update scheduling status',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const checkboxSize = compact ? 'w-3 h-3' : 'w-4 h-4';
  const iconSize = compact ? 'h-2.5 w-2.5' : 'h-3 w-3';
  const textSize = compact ? 'text-[9px]' : 'text-[10px]';

  if (localValue) {
    // Needs Scheduling - amber checkbox (checked)
    return (
      <div
        className={`flex items-center gap-1 mt-1 cursor-pointer select-none ${isUpdating ? 'opacity-50' : ''}`}
        onClick={handleToggle}
        onMouseDown={(e) => e.stopPropagation()}
        role="checkbox"
        aria-checked={true}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleToggle(e as unknown as React.MouseEvent);
          }
        }}
      >
        <div
          className={`${checkboxSize} rounded border-2 border-amber-500 bg-amber-500 flex items-center justify-center`}
        >
          <Check className={`${iconSize} text-white`} strokeWidth={3} />
        </div>
        <Calendar className={`${iconSize} text-amber-600`} />
        <span className={`${textSize} text-amber-700 dark:text-amber-400 font-medium`}>
          Needs Scheduling
        </span>
      </div>
    );
  }

  // Scheduled - green checkbox (unchecked visual but green)
  return (
    <div
      className={`flex items-center gap-1 mt-1 cursor-pointer select-none ${isUpdating ? 'opacity-50' : ''}`}
      onClick={handleToggle}
      onMouseDown={(e) => e.stopPropagation()}
      role="checkbox"
      aria-checked={false}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleToggle(e as unknown as React.MouseEvent);
        }
      }}
    >
      <div
        className={`${checkboxSize} rounded border-2 border-green-500 bg-green-500 flex items-center justify-center`}
      >
        <Check className={`${iconSize} text-white`} strokeWidth={3} />
      </div>
      <Check className={`${iconSize} text-green-600`} />
      <span className={`${textSize} text-green-700 dark:text-green-400 font-medium line-through`}>
        Scheduled
      </span>
    </div>
  );
}
